import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard/Dashboard';
import './App.css';
import { Provider } from 'react-redux';
import nex_store from './redux/store';
import { ThemeProvider } from '@emotion/react';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import customTheme from './components/Layout/theme/customTheme';
import { AppProvider } from './context/appcontext';
import NotFound from './pages/NotFound';
import { PageUrl } from './utils/app-config';
import "./index.css";
import ProtectedRoute from './utils/ProtectedRoute';

// MSAL authentication imports
import { MsalProvider, AuthenticatedTemplate, useIsAuthenticated } from "@azure/msal-react";
import ViewApplications from './pages/Settings/Application/ViewApplications';
import { msalConfig } from './authConfig';
import { PublicClientApplication } from '@azure/msal-browser';
import AuthHandler from './AuthHandler ';
import useSilentRefresh from './useSilentRefresh';

// Maintenance Wrapper import
import MaintenanceWrapper from './components/Wrappers/MaintenanceWrapper';

// Lazy-loaded components
const ApplicationPage = React.lazy(() => import('./pages/Settings/Application/Applications'));
const AddApplicationPage = React.lazy(() => import('./pages/Settings/Application/AddApplications'));
const EditApplicationPage = React.lazy(() => import('./pages/Settings/Application/EditApplications'));
const AddSubtypesPage = React.lazy(() => import('./pages/Settings/Subtypes/AddSubtypes'));
const AddConfigurationPage = React.lazy(() => import('./pages/Settings/Configuration/AddConfiguration'));
const EditFrequencyPage = React.lazy(() => import('./pages/Settings/ReminderFrequency/EditFrequency'));
const AddEditCompaniesPage = React.lazy(() => import('./pages/Settings/Companies/AddEditCompanies'));
const AddEditSubtypesPage = React.lazy(() => import('./pages/Settings/CompanySubtypes/AddEditCompanySubtypes'));
const JobsLogsPage = React.lazy(() => import('./pages/Settings/JobsLogs/JobsLogs'));
const AddEditRolesPage = React.lazy(() => import('./pages/Settings/Roles/AddEditRoles'));
const AssignRolesPage = React.lazy(() => import('./pages/Settings/Roles/AssignRoles'));
const AssignUserRolePage = React.lazy(() => import('./pages/Settings/UserManagement/AssignUserRole'));
const UserPage = React.lazy(() => import('./pages/User/User'));
const RequestPage = React.lazy(() => import('./pages/Requests/Requests'));
const PendingRequestPage = React.lazy(() => import('./pages/Requests/PendingRequests'));
const ReadRequestPage = React.lazy(() => import('./pages/Requests/ReadRequests'));
const ArchiveRequestPage = React.lazy(() => import('./pages/Requests/ArchiveRequests'));
const DuplicateRequestPage = React.lazy(() => import('./pages/Requests/DuplicateRequests'));
const OutofOfficePage = React.lazy(() => import('./pages/OutofOffice/OutofOffice'));
const SummaryReportPage = React.lazy(() => import('./pages/SummaryReport/SummaryReport'));
const SubtypesPage = React.lazy(() => import('./pages/Settings/Subtypes/Subtypes'));
const ConfigurationPage = React.lazy(() => import('./pages/Settings/Configuration/Configuration'));
const CompaniesPage = React.lazy(() => import('./pages/Settings/Companies/Companies'));
const CompanySubtypesPage = React.lazy(() => import('./pages/Settings/CompanySubtypes/CompanySubtypes'));
const ReminderFrequencyPage = React.lazy(() => import('./pages/Settings/ReminderFrequency/ReminderFrequency'));
const ProfilePage = React.lazy(() => import('./components/Layout/Profile'));
const RolesPage = React.lazy(() => import('./pages/Settings/Roles/Roles'));
const UserManagementPage = React.lazy(() => import('./pages/Settings/UserManagement/UserManagement'));
const ArchivalDashboardPage = React.lazy(() => import('./pages/Dashboard/ArchiveDashboard'));
const DivisionPage = React.lazy(() => import('./pages/Settings/Division/Division'));
const BranchPage = React.lazy(() => import('./pages/Settings/Branch/Branch'));
const CategoryPage = React.lazy(() => import('./pages/Settings/Category/Category'));
const CurrencyPage = React.lazy(() => import('./pages/Settings/Currency/Currency'));
const ExchangeRatePage = React.lazy(() => import('./pages/Settings/ExchangeRate/ExchangeRate'));
const AddDivisionPage = React.lazy(() => import('./pages/Settings/Division/AddDivision'));
const AddBranchPage = React.lazy(() => import('./pages/Settings/Branch/AddBranch'));
const AddCategoryPage = React.lazy(() => import('./pages/Settings/Category/AddCategory'));
const AddCurrencyPage = React.lazy(() => import('./pages/Settings/Currency/AddCurrency'));
const AddExchangeRatePage = React.lazy(() => import('./pages/Settings/ExchangeRate/addExchangeRate'));
const LoginPage = React.lazy(() => import('./components/Layout/Login'));
const CompanyDivisionPage = React.lazy(() => import('./pages/Settings/Division/CompanyDivision'));
const AddCompanyDivisionPage = React.lazy(() => import('./pages/Settings/Division/AddCompanyDivision'));
const CompanyBranchPage = React.lazy(() => import('./pages/Settings/Branch/CompanyBranch'));
const AddCompanyBranchPage = React.lazy(() => import('./pages/Settings/Branch/AddCompanyBranch'));
const CompanyCategoryPage = React.lazy(() => import('./pages/Settings/Category/CompanyCategory'));
const AddCompanyCategoryPage = React.lazy(() => import('./pages/Settings/Category/AddCompanyCategory'));
const CompanyExchangeRatePage = React.lazy(() => import('./pages/Settings/ExchangeRate/CompanyExchangeRate'));
const AddCompanyExchangeRatePage = React.lazy(() => import('./pages/Settings/ExchangeRate/AddCompanyExchangeRate'));
const msalInstance = new PublicClientApplication(msalConfig);

// Route configuration
const getAppRoutes = (useSimpleLogin: boolean) => [
  { path: '/', element: useSimpleLogin ? LoginPage : Dashboard, protected: !useSimpleLogin },
  { path: `/${PageUrl.DashboardPage}`, element: Dashboard, protected: true },
  { path: `/${PageUrl.UserPage}`, element: UserPage, protected: true },
  { path: `/${PageUrl.ApplicationPage}`, element: ApplicationPage, protected: true },
  { path: `/${PageUrl.RequestsPage}`, element: RequestPage, protected: true },
  { path: `/${PageUrl.PendingRequestPage}`, element: PendingRequestPage, protected: true },
  { path: `/${PageUrl.ReadRequestPage}`, element: ReadRequestPage, protected: true },
  { path: `/${PageUrl.ArchiveRequestPage}`, element: ArchiveRequestPage, protected: true },
  { path: `/${PageUrl.DuplicateRequestPage}`, element: DuplicateRequestPage, protected: true },
  { path: `/${PageUrl.OutofOfficePage}`, element: OutofOfficePage, protected: true },
  { path: `/${PageUrl.SummaryReportPage}`, element: SummaryReportPage, protected: true },
  { path: `/${PageUrl.SubtypesPage}`, element: SubtypesPage, protected: true },
  { path: `/${PageUrl.CompaniesPage}`, element: CompaniesPage, protected: true },
  { path: `/${PageUrl.CompanySubtypesPage}`, element: CompanySubtypesPage, protected: true },
  { path: `/${PageUrl.ReminderFrequencyPage}`, element: ReminderFrequencyPage, protected: true },
  { path: `/${PageUrl.ProfilePage}`, element: ProfilePage, protected: true },
  { path: `/${PageUrl.AddApplicationPage}`, element: AddApplicationPage, protected: true },
  { path: `/${PageUrl.EditApplicationsPage}/:id`, element: EditApplicationPage, protected: true },
  { path: `/${PageUrl.AddSubtypesPage}`, element: AddSubtypesPage, protected: true },
  { path: `/${PageUrl.ViewApplicationPage}/:id`, element: ViewApplications, protected: true },
  { path: `/${PageUrl.JobsLogsPage}`, element: JobsLogsPage, protected: true },
  { path: `/${PageUrl.ConfigurationPage}`, element: ConfigurationPage, protected: true },
  { path: `/${PageUrl.AddConfigurationPage}`, element: AddConfigurationPage, protected: true },
  { path: `/${PageUrl.EditFrequencyPage}`, element: EditFrequencyPage, protected: true },
  { path: `/${PageUrl.AddEditCompaniesPage}`, element: AddEditCompaniesPage, protected: true },
  { path: `/${PageUrl.AddEditSubtypesPage}`, element: AddEditSubtypesPage, protected: true },
  { path: `/${PageUrl.RolesPage}`, element: RolesPage, protected: true },
  { path: `/${PageUrl.AddEditRolesPage}`, element: AddEditRolesPage, protected: true },
  { path: `/${PageUrl.AssignRolesPage}`, element: AssignRolesPage, protected: true },
  { path: `/${PageUrl.UserManagementPage}`, element: UserManagementPage, protected: true },
  { path: `/${PageUrl.ArchivalDashboardPage}`, element: ArchivalDashboardPage, protected: true },
  { path: `/${PageUrl.AssignUserRolePage}`, element: AssignUserRolePage, protected: true },
  { path: `/${PageUrl.DivisionPage}`, element: DivisionPage, protected: true },
  { path: `/${PageUrl.BranchPage}`, element: BranchPage, protected: true },
  { path: `/${PageUrl.CategoryPage}`, element: CategoryPage, protected: true },
  { path: `/${PageUrl.CurrencyPage}`, element: CurrencyPage, protected: true },
  { path: `/${PageUrl.ExchangeRatePage}`, element: ExchangeRatePage, protected: true },
  { path: `/${PageUrl.AddDivisionPage}`, element: AddDivisionPage, protected: true },
  { path: `/${PageUrl.AddBranchPage}`, element: AddBranchPage, protected: true },
  { path: `/${PageUrl.AddCategoryPage}`, element: AddCategoryPage, protected: true },
  { path: `/${PageUrl.AddCurrencyPage}`, element: AddCurrencyPage, protected: true },
  { path: `/${PageUrl.AddExchangeRatePage}`, element: AddExchangeRatePage, protected: true },
  { path: `/${PageUrl.CompanyDivisionPage}`, element: CompanyDivisionPage, protected: true },
  { path: `/${PageUrl.AddCompanyDivisionPage}`, element: AddCompanyDivisionPage, protected: true },
  { path: `/${PageUrl.CompanyBranchPage}`, element: CompanyBranchPage, protected: true },
  { path: `/${PageUrl.AddCompanyBranchPage}`, element: AddCompanyBranchPage, protected: true },
  { path: `/${PageUrl.CompanyCategoryPage}`, element: CompanyCategoryPage, protected: true },
  { path: `/${PageUrl.AddCompanyCategoryPage}`, element: AddCompanyCategoryPage, protected: true },
  { path: `/${PageUrl.CompanyExchangeRatePage}`, element: CompanyExchangeRatePage, protected: true },
  { path: `/${PageUrl.AddCompanyExchangeRatePage}`, element: AddCompanyExchangeRatePage, protected: true },
  { path: `/${PageUrl.LoginPage}`, element: LoginPage, protected: false }
];

interface AppLayoutProps {
  useSimpleLogin: boolean;
}

const AppLayout: React.FC<AppLayoutProps> = ({ useSimpleLogin }) => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const appRoutes = getAppRoutes(useSimpleLogin);

  useEffect(() => {
    if (useSimpleLogin && location.pathname === '/') {
      navigate('/login', { replace: true });
    } else if (!useSimpleLogin && !isAuthenticated && !isLoginPage) {
      navigate('/login', { replace: true });
    } else if (isAuthenticated && isLoginPage) {
      navigate(`/${PageUrl.DashboardPage}`, { replace: true });
    }
  }, [isAuthenticated, isLoginPage, navigate, useSimpleLogin, location.pathname]);

  const shouldShowLayout = !isLoginPage && (useSimpleLogin || isAuthenticated);

  const renderRoutes = () => {
    return (
      <Routes>
        {appRoutes.map((route, index) => {
          if (useSimpleLogin && route.path === '/') return null;

          if (route.protected && !useSimpleLogin && !isAuthenticated) {
            return <Route key={index} path={route.path} element={<Navigate to="/login" replace />} />;
          }

          return (
            <Route
              key={index}
              path={route.path}
              element={
                <React.Suspense fallback={<></>}>
                  {route.protected ? (
                    <ProtectedRoute element={<route.element />} />
                  ) : (
                    <route.element />
                  )}
                </React.Suspense>
              }
            />
          );
        })}
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  };

  return (
    <>
      {shouldShowLayout ? (
        <>
          <div className='topPositionOOOffice'>
            <div className='sidebar'>
              <Sidebar />
            </div>
          </div>
          <div>
            <div className='content-area' style={{ flex: 1, padding: '2.5rem 1.7rem', overflowY: 'auto' }}>
              <Header />
              <div className='mainStartPanel'>{renderRoutes()}</div>
            </div>
          </div>
        </>
      ) : (
        <div className='mainStartPanel'>{renderRoutes()}</div>
      )}
    </>
  );
};

// Fetch login flags
export const fetchSimpleLogin = async (): Promise<boolean> => {
  try {
    const url = process.env.REACT_APP_API_URL + "/Configuration/CheckLogin";
    const response = await axios.post(url);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching simple login:", error);
    return false;
  }
};

export const fetchMaintenance = async (): Promise<boolean> => {
  try {
    const url = process.env.REACT_APP_API_URL + "/Configuration/CheckMaintanceMode";
    
    const response = await axios.get(url);
    return response.data?.isEnabled;
  } catch (error) {
    console.error("Error fetching maintenance:", error);
    return true;
  }
};

const SilentRefreshHandler: React.FC = () => {
  useSilentRefresh();
  return null;
};

const App: React.FC = () => {
  const [useSimpleLogin, setUseSimpleLogin] = useState<boolean | null>(null);
  const [maintenance, setMaintenance] = useState<boolean>(false);
  const [msalInitialized, setMsalInitialized] = useState(false);

  useEffect(() => {
    const load = async () => {
      const login = await fetchSimpleLogin();
      const maintenanceFlag = await fetchMaintenance();
      setUseSimpleLogin(login);
      setMaintenance(maintenanceFlag);
    };
    load();
  }, []);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();
        setMsalInitialized(true);
      } catch (error) {
        console.error("MSAL initialization failed:", error);
        setMsalInitialized(false);
      }
    };

    if (useSimpleLogin === false) initializeMsal();
    else setMsalInitialized(true);

  }, [useSimpleLogin]);

  if (useSimpleLogin === null || !msalInitialized) {
    return (
      <div className="loader-container-req">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <Provider store={nex_store}>
        <AppProvider>
          <Router>
            {useSimpleLogin ? (
              <MaintenanceWrapper showMaintenance={maintenance}>
                <AppLayout useSimpleLogin={useSimpleLogin} />
              </MaintenanceWrapper>
            ) : (
              <MsalProvider instance={msalInstance}>
                <AuthHandler />
                <SilentRefreshHandler />
                <AuthenticatedTemplate>
                  <MaintenanceWrapper showMaintenance={maintenance}>
                    <AppLayout useSimpleLogin={useSimpleLogin} />
                  </MaintenanceWrapper>
                </AuthenticatedTemplate>
              </MsalProvider>
            )}
          </Router>
        </AppProvider>
      </Provider>
    </ThemeProvider>
  );
};

export default App;
